/**
 * AnalyticsService — typed facade over PostHog. `capture(event, props)` checks
 * `props` against `EventSchema[event]`, so typos and missing fields fail to
 * compile.
 */
import { Injectable, inject } from '@angular/core';
import { PosthogService } from './posthog.service';
import type { AnalyticsEvent, EventSchema } from './events';

type Props = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly posthog = inject(PosthogService);

  capture<E extends AnalyticsEvent>(event: E, props: EventSchema[E]): void {
    this.posthog.posthog.capture(event, props as unknown as Props);
  }

  /**
   * For events fired right before this page navigates away (post-login
   * redirect, logout): sent immediately over sendBeacon so the unload doesn't
   * drop them from the batch queue.
   */
  captureBeforeNavigation<E extends AnalyticsEvent>(event: E, props: EventSchema[E]): void {
    this.posthog.posthog.capture(event, props as unknown as Props, {
      send_instantly: true,
      transport: 'sendBeacon',
    });
  }

  /** Same `customer` group type the other apps use, keyed by customer id. */
  group(groupType: string, groupKey: string, props?: Props): void {
    this.posthog.posthog.group(groupType, groupKey, props);
  }

  captureException(err: unknown, props?: Props): void {
    const error = err instanceof Error ? err : new Error(String(err));
    this.posthog.posthog.captureException(error, props);
  }

  /** New anonymous id, so the next person on this device isn't merged with the last. */
  reset(): void {
    this.posthog.reset();
  }
}
